/**
 * Pure logic for the website CMS screens (pages, section editor, media, collections, prices). The server (qm-api
 * /v1/cms and /v1/billing/admin/prices) enforces every rule; these helpers decide what the console offers and keep
 * the editor operations testable without a DOM.
 */
import { checkOverride, type OverrideCheck } from '@qm/i18n';
import type { UiTextOverrideView } from '@qm/contracts/cms-text';
import {
  CMS_IMAGE_TYPES, CMS_MAX_IMAGE_BYTES, CMS_MAX_VIDEO_BYTES, CMS_SECTION_TYPES, CMS_VIDEO_TYPES, cmsMediaPath,
  type CmsLocale, type CmsPageDoc, type CmsSection,
} from '@qm/contracts/cms';

// ------------------------------------------------------------------ roles (mirror apps/api/src/modules/cms)
const has = (roles: readonly string[], ...r: string[]) => roles.some((x) => r.includes(x));
/** View pages, media and collections. staff_finance only sees the Prices screen. */
export const canViewCms = (roles: readonly string[]) => has(roles, 'staff_admin', 'staff_content', 'staff_ops', 'staff_support', 'staff_readonly');
/** Edit and publish pages, items and media (Super Admin + content editors). */
export const canEditCms = (roles: readonly string[]) => has(roles, 'staff_admin', 'staff_content');
/** Create/remove collections and categories: Super Admin only. */
export const canManageCollections = (roles: readonly string[]) => has(roles, 'staff_admin');
/** Delete media: Super Admin only. */
export const canDeleteMedia = (roles: readonly string[]) => has(roles, 'staff_admin');
/** Open the Prices screen and propose a change. */
export const canProposePrice = (roles: readonly string[]) => has(roles, 'staff_admin', 'staff_content', 'staff_finance');

export interface PriceChangeLike { status: string; proposedBy: string; kind: 'price' | 'wholesale' }
/** Four-eyes: Finance approves others' proposals only; Finance or Super Admin reject; the proposer withdraws. */
export function priceChangeActions(c: PriceChangeLike, roles: readonly string[], userId: string | null | undefined) {
  const open = c.status === 'proposed';
  const own = !!userId && c.proposedBy === userId;
  return {
    approve: open && has(roles, 'staff_finance') && !own,
    reject: open && has(roles, 'staff_finance', 'staff_admin'),
    withdraw: open && own && canProposePrice(roles),
    needsStripeId: c.kind === 'price',
    own,
  };
}

// ------------------------------------------------------------------ website origin
/** Website hosts per environment (qm-infra environments/<env>.json hosts.website). */
export const WEBSITE_ORIGINS = { dev: 'https://www-dev.quixomarketing.com', stg: 'https://www-stg.quixomarketing.com', prod: 'https://quixomarketing.com' } as const;
/**
 * The public website origin of the same environment as the portal host (same rule as the API's SITE maps):
 * admin.quixomarketing.com -> quixomarketing.com, admin-stg.x -> www-stg.x, admin-dev.x -> www-dev.x. Unknown hosts
 * (localhost, previews) use the dev website so a local console never edits against production media.
 */
export function websiteOriginFor(host: string | null | undefined): string {
  const h = (host ?? '').toLowerCase().split(':')[0] ?? '';
  const [first = '', ...rest] = h.split('.');
  const m = /^(admin|portal|partners)(?:-(dev|stg))?$/.exec(first);
  if (!m || rest.join('.') !== 'quixomarketing.com') return WEBSITE_ORIGINS.dev;
  return m[2] === 'dev' ? WEBSITE_ORIGINS.dev : m[2] === 'stg' ? WEBSITE_ORIGINS.stg : WEBSITE_ORIGINS.prod;
}
export const currentWebsiteOrigin = () => websiteOriginFor((globalThis as { location?: { host?: string } }).location?.host);
/**
 * Thumbnail / preview URL of a media file. The staff console loads it same-origin through the BFF
 * (/bff/v1/cms/public/media/<id>), so the portal CSP (img-src 'self') needs no website origin.
 */
export const mediaPreviewUrl = (id: string) => `/bff/v1/cms/public/media/${encodeURIComponent(id)}`;
/** Public URL of a media file on the website (what visitors load). */
export const websiteMediaUrl = (origin: string, id: string) => `${origin}${cmsMediaPath(id)}`;

// ------------------------------------------------------------------ cms-source (seed from the live site)
export interface CmsSourceEntry { path: string; slug: string; hasAr: boolean; locked: boolean; title: { en: string | null; ar: string | null } }
export function parseSourceIndex(json: unknown): CmsSourceEntry[] {
  const list = Array.isArray(json) ? json : Array.isArray((json as { pages?: unknown })?.pages) ? (json as { pages: unknown[] }).pages : [];
  return list.flatMap((e) => {
    const o = e as Record<string, unknown>;
    if (typeof o?.path !== 'string' || typeof o.slug !== 'string') return [];
    const title = (o.title ?? {}) as Record<string, unknown>;
    return [{
      path: o.path, slug: o.slug, hasAr: o.hasAr === true, locked: o.locked === true,
      title: { en: typeof title.en === 'string' ? title.en : null, ar: typeof title.ar === 'string' ? title.ar : null },
    }];
  });
}
/** Every (page, language) on the live site that the CMS has no page for yet. Locked (code-managed) pages are skipped. */
export function pendingImports(index: readonly CmsSourceEntry[], existing: readonly { path: string; lang: CmsLocale }[]): { entry: CmsSourceEntry; lang: CmsLocale }[] {
  const have = new Set(existing.map((p) => `${p.lang}:${p.path}`));
  return index.flatMap((entry) => (entry.locked ? [] : (['en', 'ar'] as const)
    .filter((lang) => (lang === 'en' || entry.hasAr) && !have.has(`${lang}:${entry.path}`))
    .map((lang) => ({ entry, lang }))));
}
/** The editable doc for one language from /cms-source/<slug>.json ({ path, en, ar }). */
export function sourceDoc(file: unknown, lang: CmsLocale): CmsPageDoc | null {
  const d = (file as Record<string, unknown> | null)?.[lang] as Record<string, unknown> | null | undefined;
  if (!d || typeof d.title !== 'string' || !Array.isArray(d.sections)) return null;
  const og = d.og && typeof d.og === 'object' ? Object.fromEntries(['title', 'description'].filter((k) => typeof (d.og as Record<string, unknown>)[k] === 'string').map((k) => [k, (d.og as Record<string, string>)[k]!])) : null;
  return {
    title: d.title, description: typeof d.description === 'string' ? d.description : '',
    ...(typeof d.noindex === 'boolean' ? { noindex: d.noindex } : {}),
    ...(og && Object.keys(og).length ? { og } : {}),
    sections: d.sections as CmsSection[],
  };
}

// ------------------------------------------------------------------ JSON values
export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };
export type JsonObject = { [k: string]: Json };
export type JsonPath = readonly (string | number)[];
const isObj = (v: unknown): v is JsonObject => !!v && typeof v === 'object' && !Array.isArray(v);
export const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export function getIn(v: Json | undefined, path: JsonPath): Json | undefined {
  let cur: Json | undefined = v;
  for (const k of path) {
    if (Array.isArray(cur) && typeof k === 'number') cur = cur[k];
    else if (isObj(cur) && typeof k === 'string') cur = cur[k];
    else return undefined;
  }
  return cur;
}
/** Immutable set at any depth; missing containers are created (array for a numeric key, object otherwise). */
export function setIn<T extends Json>(v: T, path: JsonPath, value: Json): T {
  if (path.length === 0) return value as T;
  const [k, ...rest] = path as [string | number, ...(string | number)[]];
  if (Array.isArray(v) && typeof k === 'number') {
    const out = [...v];
    out[k] = setIn((v[k] ?? (typeof rest[0] === 'number' ? [] : {})) as Json, rest, value);
    return out as T;
  }
  const base: JsonObject = isObj(v) ? v : {};
  return { ...base, [String(k)]: setIn((base[String(k)] ?? (typeof rest[0] === 'number' ? [] : {})) as Json, rest, value) } as T;
}
/** Immutable remove of an object key or array index. */
export function removeIn<T extends Json>(v: T, path: JsonPath): T {
  if (path.length === 0) return v;
  const parent = getIn(v, path.slice(0, -1));
  const k = path[path.length - 1]!;
  if (Array.isArray(parent) && typeof k === 'number') return setIn(v, path.slice(0, -1), parent.filter((_, i) => i !== k));
  if (isObj(parent) && typeof k === 'string') {
    const { [k]: _gone, ...keep } = parent;
    return setIn(v, path.slice(0, -1), keep);
  }
  return v;
}
/** Move an array element up (-1) or down (+1); out-of-range moves return the list unchanged. */
export function moveItem<T>(list: readonly T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (i < 0 || i >= list.length || j < 0 || j >= list.length) return [...list];
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

export const VALUE_KINDS = ['text', 'number', 'boolean', 'list', 'group'] as const;
export type ValueKind = (typeof VALUE_KINDS)[number];
export function newValue(kind: ValueKind): Json {
  return kind === 'number' ? 0 : kind === 'boolean' ? false : kind === 'list' ? [] : kind === 'group' ? {} : '';
}
/** An empty value of the same shape (used when adding a list item next to existing ones). */
export function blankLike(v: Json | undefined): Json {
  if (typeof v === 'string' || v === null || v === undefined) return '';
  if (typeof v === 'number') return 0;
  if (typeof v === 'boolean') return false;
  if (Array.isArray(v)) return [];
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, blankLike(x)]));
}
/** The next item for a list: a blank copy of the last item's shape, or text for an empty list. */
export const nextListItem = (list: readonly Json[], kind: ValueKind = 'text'): Json => (list.length ? blankLike(list[list.length - 1]) : newValue(kind));

/** Section field keys (the contract allows any record key up to 64 chars; the editor asks for identifier-like keys). */
export const FIELD_KEY_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
export type AddFieldResult = { ok: true; value: JsonObject } | { ok: false; reason: 'invalid_key' | 'duplicate_key' | 'reserved_key' };
export function addField(obj: JsonObject, key: string, kind: ValueKind, opts: { reserved?: readonly string[] } = {}): AddFieldResult {
  const k = key.trim();
  if (!FIELD_KEY_RE.test(k)) return { ok: false, reason: 'invalid_key' };
  if (opts.reserved?.includes(k)) return { ok: false, reason: 'reserved_key' };
  if (Object.prototype.hasOwnProperty.call(obj, k)) return { ok: false, reason: 'duplicate_key' };
  return { ok: true, value: { ...obj, [k]: newValue(kind) } };
}
export const isMediaField = (key: string | number | undefined) => key === 'mediaId' || key === 'posterId';
/** Text inputs become textareas for long or multi-line copy. */
export const isLongText = (s: string, key?: string | number) => s.length > 80 || s.includes('\n') || key === 'body' || key === 'a' || key === 'sub' || key === 'fineprint';

// ------------------------------------------------------------------ sections
export type SectionType = (typeof CMS_SECTION_TYPES)[number];
export const SECTION_TYPES: readonly SectionType[] = CMS_SECTION_TYPES;
const TEMPLATES: Partial<Record<SectionType, JsonObject>> = {
  hero: { h1: '', eyebrow: '', sub: '' },
  'hero-field': { h1: '', eyebrow: '', sub: '' },
  'rich-text': { heading: '', body: '' },
  image: { mediaId: '', alt: '', caption: '' },
  video: { mediaId: '', posterId: '', caption: '' },
  gallery: { heading: '', items: [{ mediaId: '', alt: '' }] },
  collection: { heading: '', collection: '', category: '' },
  cta: { heading: '', ctaPrimary: '', ctaPrimaryHref: '', ctaSecondary: '', ctaSecondaryHref: '' },
  faq: { heading: '', items: [{ q: '', a: '' }] },
  plans: { heading: '', sub: '', plans: [{ name: '', price: '', altPrice: '', note: '', cta: '', ctaHref: '' }], fineprint: '' },
};
export function sectionTemplate(type: SectionType): CmsSection {
  return { type, ...clone(TEMPLATES[type] ?? { heading: '' }) } as CmsSection;
}
/** Insert a new section of `type` after index `after` (default: at the end). */
export function addSection(sections: readonly CmsSection[], type: SectionType, after?: number): CmsSection[] {
  const at = after === undefined ? sections.length : Math.min(Math.max(after + 1, 0), sections.length);
  return [...sections.slice(0, at), sectionTemplate(type), ...sections.slice(at)];
}
export const removeSection = (sections: readonly CmsSection[], i: number): CmsSection[] => sections.filter((_, j) => j !== i);
export const moveSection = (sections: readonly CmsSection[], i: number, dir: -1 | 1): CmsSection[] => moveItem(sections, i, dir);
/** Deep copy placed right after the original; the copy drops its anchor id so ids stay unique. */
export function duplicateSection(sections: readonly CmsSection[], i: number): CmsSection[] {
  const s = sections[i];
  if (!s) return [...sections];
  const { id: _id, ...copy } = clone(s);
  return [...sections.slice(0, i + 1), copy as CmsSection, ...sections.slice(i + 1)];
}
/** Edit a field inside section i at any depth (path relative to the section). */
export function editSectionField(sections: readonly CmsSection[], i: number, path: JsonPath, value: Json): CmsSection[] {
  return sections.map((s, j) => (j === i ? setIn(s as unknown as JsonObject, path, value) as unknown as CmsSection : s));
}
export function removeSectionField(sections: readonly CmsSection[], i: number, path: JsonPath): CmsSection[] {
  return sections.map((s, j) => (j === i ? removeIn(s as unknown as JsonObject, path) as unknown as CmsSection : s));
}
/** The site renderer needs exactly one hero (the page's h1); the server refuses publish otherwise (one_h1). */
export const h1Count = (doc: Pick<CmsPageDoc, 'sections'>) => doc.sections.filter((s) => s.type === 'hero' || s.type === 'hero-field').length;
export function newPageDoc(title: string): CmsPageDoc {
  return { title, description: '', sections: [sectionTemplate('hero')] };
}

// ------------------------------------------------------------------ money (integer minor units)
const EXP: Record<string, number> = { USD: 2, AED: 2, EUR: 2, GBP: 2, SAR: 2, JPY: 0, KWD: 3, BHD: 3, OMR: 3 };
export const currencyExponent = (currency: string) => EXP[currency.toUpperCase()] ?? 2;
/** "1,299.5" AED -> 129950. Parsed as a string (no float drift); null when not a valid amount for the currency. */
export function toMinor(major: string, currency: string): number | null {
  const s = major.trim().replace(/,/g, '');
  const exp = currencyExponent(currency);
  const m = /^(\d+)(?:\.(\d*))?$/.exec(s);
  if (!m) return null;
  const frac = m[2] ?? '';
  if (frac.length > exp) return null;
  const n = Number(m[1]) * 10 ** exp + Number((frac + '0'.repeat(exp)).slice(0, exp) || '0');
  return Number.isSafeInteger(n) ? n : null;
}
/** 129950 AED -> "1299.50" (for form inputs; display uses formatMoney). */
export function toMajor(minor: number, currency: string): string {
  const exp = currencyExponent(currency);
  const neg = minor < 0 ? '-' : '';
  const a = Math.abs(Math.trunc(minor));
  if (exp === 0) return `${neg}${a}`;
  const s = String(a).padStart(exp + 1, '0');
  return `${neg}${s.slice(0, -exp)}.${s.slice(-exp)}`;
}

// ------------------------------------------------------------------ uploads
export type UploadCheck = { ok: true; kind: 'image' | 'video' } | { ok: false; reason: 'unsupported_type' | 'too_large' | 'empty'; maxBytes?: number };
/** Same limits as the API: images 10 MB (png, jpeg, webp, gif, avif), video 50 MB (mp4, webm), never SVG. */
export function validateUpload(f: { type: string; size: number }): UploadCheck {
  const type = f.type.toLowerCase();
  const image = (CMS_IMAGE_TYPES as readonly string[]).includes(type);
  const video = (CMS_VIDEO_TYPES as readonly string[]).includes(type);
  if (!image && !video) return { ok: false, reason: 'unsupported_type' };
  if (f.size <= 0) return { ok: false, reason: 'empty' };
  const max = image ? CMS_MAX_IMAGE_BYTES : CMS_MAX_VIDEO_BYTES;
  if (f.size > max) return { ok: false, reason: 'too_large', maxBytes: max };
  return { ok: true, kind: image ? 'image' : 'video' };
}
export const megabytes = (bytes: number) => Math.round((bytes / (1024 * 1024)) * 10) / 10;

// ------------------------------------------------------------------ collections
/** Item free-form field keys (CmsItemInputSchema). */
export const ITEM_FIELD_KEY_RE = /^[a-z][a-zA-Z0-9_]{0,39}$/;
export const COLLECTION_SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const CMS_PATH_RE = /^\/([a-z0-9-]+(\/[a-z0-9-]+)*)?$/;
export function itemsPerCategory(items: readonly { categoryId: string | null }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const i of items) if (i.categoryId) out[i.categoryId] = (out[i.categoryId] ?? 0) + 1;
  return out;
}

// ------------------------------------------------------------------ Portal text (UI wording overrides)
// The API (/v1/cms/admin/ui-text) enforces every rule; these helpers decide what the screen offers and mirror the
// text rules (@qm/i18n checkOverride) so an editor gets feedback before saving.
/** View the Portal text screen. Same rule as the rest of the website CMS. */
export const canViewUiText = canViewCms;
/** Save drafts, publish, roll back and reset portal text: Super Admin and content editors. */
export const canEditUiText = canEditCms;

export const UI_TEXT_PAGE_SIZE = 25;

/** shipped = no override; draft = saved but not live; published = live and current; pending = live, with a newer unpublished draft. */
export type UiTextRowStatus = 'shipped' | 'draft' | 'published' | 'pending';
export function uiTextStatus(o: UiTextOverrideView | null): UiTextRowStatus {
  if (!o) return 'shipped';
  if (o.status === 'published') return 'published';
  return o.publishedVersion === null ? 'draft' : 'pending';
}
export const UI_TEXT_STATUS_TONE: Record<UiTextRowStatus, 'neutral' | 'warn' | 'good' | 'accent'> = { shipped: 'neutral', draft: 'warn', published: 'good', pending: 'accent' };

/** What the editor box starts with: the saved override, else the shipped wording. */
export const uiTextStart = (shipped: string, o: UiTextOverrideView | null): string => (o ? o.value : shipped);
/** Unsaved changes: the trimmed box differs from the saved override (or the shipped text when there is none). */
export const uiTextDirty = (shipped: string, o: UiTextOverrideView | null, value: string): boolean => value.trim() !== uiTextStart(shipped, o);
/** null when the text may be saved. Same text as the shipped wording with no override is "nothing to save", not an error. */
export function uiTextProblem(shipped: string, value: string): OverrideCheck | null { return checkOverride(shipped, value.trim()); }

export interface UiTextRowActions { save: boolean; publish: boolean; reset: boolean; history: boolean }
/** Which buttons a locale editor offers: save needs a valid change; publish needs a saved, unpublished draft and no unsaved edit. */
export function uiTextActions(o: UiTextOverrideView | null, shipped: string, value: string, canEdit: boolean): UiTextRowActions {
  const dirty = uiTextDirty(shipped, o, value);
  return {
    save: canEdit && dirty && !uiTextProblem(shipped, value),
    publish: canEdit && !!o && o.status === 'draft' && !dirty,
    reset: canEdit && !!o,
    history: !!o,
  };
}

/** Message key for a client-side problem (the same reasons the API returns). */
export const uiTextProblemKey = (p: OverrideCheck): string => `portal.admin.cms.text.err.${p.reason}`;
